<template>
    <div>
        <label
            for="promotional_code"
            class="text-sm text-gray-500"
        >
            Promotional Code
        </label>

        <div class="flex items-center gap-2">
            <input-text
                id="promotional_code"
                v-model="promotionalCode"
                placeholder="Enter Code"
                :disabled="isLoading"
                @keydown.enter="handleApplyCode"
            />

            <prime-button
                :disabled="promotionalCode === ''"
                :loading="isLoading"
                label="Apply"
                @click="handleApplyCode"
            />
        </div>

        <div class="text-red-600 text-sm">
            {{ promoCodeError }}
        </div>

        <div class="flex flex-col gap-2">
            <prime-message
                v-for="(
                    appliedPromotionalCode, appliedPromotionalCodeIndex
                ) in modelValue"
                :key="appliedPromotionalCode.code"
                severity="success"
                icon="pi pi-tag"
                @close="handleRemoveCode(appliedPromotionalCodeIndex)"
            >
                <span class="font-semibold">{{
                    appliedPromotionalCode.fullCode
                }}</span>
                <template v-if="appliedPromotionalCode.description !== ''">
                    <br />
                    {{ appliedPromotionalCode.description }}
                </template>
            </prime-message>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import PrimeMessage from 'primevue/message'
import InputText from 'primevue/inputtext'
import PrimeButton from 'primevue/button'
import type { AppliedPromotionalCode } from '~/components/PurchaseContract/promotional-code'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useViewer } from '#imports'
import { useRoute } from '#app'
import { useRouter } from 'vue-router'

const props = defineProps<{
    modelValue: AppliedPromotionalCode[]
    planId: string
}>()

const emit = defineEmits<{
    (e: 'update:modelValue', payload: AppliedPromotionalCode[]): void
}>()

const activeFacility = useActiveFacility()
const rpQuery = useRpQuery()
const promotionalCode = ref<string>('')
const lookupPromotionalCode = ref<string>('')
const viewer = useViewer()
const customerId = computed(() => viewer.value?.customerId ?? null)

const isLoading = ref(false)
const promoCodeError = ref('')

async function handleApplyCode() {
    if (isLoading.value === true) {
        return
    }

    lookupPromotionalCode.value = promotionalCode.value

    // Nothing to lookup
    if (lookupPromotionalCode.value === '') {
        return
    }

    if (
        props.modelValue.some(
            data =>
                data.fullCode.toLowerCase() ===
                lookupPromotionalCode.value.toLowerCase()
        )
    ) {
        promoCodeError.value = 'Code is already added'
        ;(
            document.getElementById('promotional_code') as HTMLInputElement
        )?.select?.()
        return
    }

    try {
        const response = await lookUpCode(lookupPromotionalCode.value)

        if (
            response.promotionCodeLookup.__typename ===
            'PromotionCodeLookupSuccess'
        ) {
            promotionalCode.value = ''
            lookupPromotionalCode.value = ''
            emit('update:modelValue', [
                ...props.modelValue,
                {
                    code: response.promotionCodeLookup.promotionCode.code,
                    fullCode: response.promotionCodeLookup.fullCode,
                    description:
                        response.promotionCodeLookup.promotionCode.promotion
                            .translatedDescription
                }
            ])
        }
        // Promo code not found
        else {
            promoCodeError.value = response.promotionCodeLookup.message
            ;(
                document.getElementById('promotional_code') as HTMLInputElement
            )?.select?.()
        }
    } finally {
        isLoading.value = false
    }
}

// When input box changes, remove error code
watch(promotionalCode, () => {
    promoCodeError.value = ''
})

async function handleRemoveCode(appliedPromotionalCodeIndex: number) {
    emit('update:modelValue', [
        ...props.modelValue.slice(0, appliedPromotionalCodeIndex),
        ...props.modelValue.slice(appliedPromotionalCodeIndex + 1)
    ])
}

const route = useRoute()
const router = useRouter()
onMounted(async () => {
    const routeCode = route.query.promo
    if (
        routeCode !== undefined &&
        routeCode !== null &&
        routeCode !== '' &&
        !Array.isArray(routeCode)
    ) {
        if (
            props.modelValue.some(
                data => data.fullCode.toLowerCase() === routeCode.toLowerCase()
            )
        ) {
            return
        }

        const response = await lookUpCode(routeCode)

        if (
            response.promotionCodeLookup.__typename ===
            'PromotionCodeLookupSuccess'
        ) {
            emit('update:modelValue', [
                ...props.modelValue,
                {
                    code: response.promotionCodeLookup.promotionCode.code,
                    fullCode: response.promotionCodeLookup.fullCode,
                    description:
                        response.promotionCodeLookup.promotionCode.promotion
                            .translatedDescription
                }
            ])
        }
        // Promo code not found
        else {
            promoCodeError.value =
                routeCode + ': ' + response.promotionCodeLookup.message
            ;(
                document.getElementById('promotional_code') as HTMLInputElement
            )?.select?.()
        }

        // Remove promo from URL
        await router.replace({
            ...route,
            query: {
                ...route.query,
                promo: undefined
            }
        })
    }
})

async function lookUpCode(code: string) {
    return await rpQuery(
        graphql(`
            query PromotionalCodeLookupQuery(
                $filter: PromotionCodeLookupFilter!
            ) {
                promotionCodeLookup(filter: $filter) {
                    __typename
                    ... on PromotionCodeLookupError {
                        message
                    }
                    ... on PromotionCodeLookupSuccess {
                        fullCode
                        promotionCode {
                            id
                            code
                            promotion {
                                translatedDescription(language: ENGLISH)
                            }
                        }
                    }
                }
            }
        `),
        {
            filter: {
                code: code,
                facilityId: activeFacility.value.id,
                planId: props.planId,
                customerId: customerId.value
            }
        }
    )
}
</script>
